<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="visible"
    width="600px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-position="right"
      label-width="80px"
    >
      <el-form-item label="任务标题" prop="title">
        <el-input v-model="form.title" placeholder="请输入任务标题" />
      </el-form-item>

      <el-form-item label="所属列" prop="column_id">
        <el-select v-model="form.column_id" placeholder="请选择所属列" style="width: 100%;">
          <el-option
            v-for="col in columns"
            :key="col.id"
            :label="col.name"
            :value="col.id"
          />
        </el-select>
      </el-form-item>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="负责人">
            <el-select v-model="form.assignee_id" placeholder="请选择负责人" clearable style="width: 100%;">
              <el-option
                v-for="user in userList"
                :key="user.id"
                :label="user.nickname"
                :value="user.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="优先级">
            <el-select v-model="form.priority" placeholder="请选择优先级" style="width: 100%;">
              <el-option label="低" :value="0" />
              <el-option label="中" :value="1" />
              <el-option label="高" :value="2" />
              <el-option label="紧急" :value="3" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="开始日期">
            <el-date-picker
              v-model="form.start_date"
              type="date"
              placeholder="选择开始日期"
              value-format="yyyy-MM-dd"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="截止日期">
            <el-date-picker
              v-model="form.due_date"
              type="date"
              placeholder="选择截止日期"
              value-format="yyyy-MM-dd"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="预估工时">
            <el-input-number v-model="form.estimated_hours" :min="0" :precision="1" style="width: 100%;" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="实际工时">
            <el-input-number v-model="form.actual_hours" :min="0" :precision="1" style="width: 100%;" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="标签">
        <el-select v-model="form.label_ids" multiple placeholder="请选择标签" style="width: 100%;">
          <el-option
            v-for="label in labelList"
            :key="label.id"
            :label="label.name"
            :value="label.id"
          >
            <span :style="{ backgroundColor: label.color, padding: '2px 8px', borderRadius: '3px', color: '#fff', fontSize: '12px' }">{{ label.name }}</span>
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="任务描述">
        <el-input
          v-model="form.description"
          type="textarea"
          :rows="4"
          placeholder="请输入任务描述"
        />
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button v-if="isEdit" type="danger" plain @click="handleDelete">删除</el-button>
      <div style="flex: 1;"></div>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getTaskDetail, createTask, updateTask, deleteTask, getLabels } from '@/api/task'
import { getAdmins } from '@/api/system'

export default {
  name: 'TaskForm',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    taskId: {
      type: [Number, null],
      default: null
    },
    columnId: {
      type: [Number, null],
      default: null
    },
    boardId: {
      type: [Number, null],
      default: null
    },
    projectId: {
      type: [Number, null],
      default: null
    }
  },
  data() {
    return {
      submitting: false,
      columns: [],
      userList: [],
      labelList: [],
      form: {
        id: null,
        title: '',
        description: '',
        column_id: null,
        assignee_id: null,
        priority: 1,
        start_date: null,
        due_date: null,
        estimated_hours: null,
        actual_hours: null,
        label_ids: []
      },
      rules: {
        title: [{ required: true, message: '请输入任务标题', trigger: 'blur' }],
        column_id: [{ required: true, message: '请选择所属列', trigger: 'change' }]
      }
    }
  },
  computed: {
    isEdit() {
      return !!this.taskId
    },
    dialogTitle() {
      return this.isEdit ? '编辑任务' : '新建任务'
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initData()
      }
    }
  },
  methods: {
    async initData() {
      this.getColumns()
      this.getUserList()
      this.getLabelList()

      if (this.isEdit) {
        await this.loadTaskDetail()
      } else {
        this.form = {
          id: null,
          title: '',
          description: '',
          column_id: this.columnId,
          assignee_id: null,
          priority: 1,
          start_date: null,
          due_date: null,
          estimated_hours: null,
          actual_hours: null,
          label_ids: []
        }
        this.$nextTick(() => {
          this.$refs.form.clearValidate()
        })
      }
    },
    async loadTaskDetail() {
      try {
        const res = await getTaskDetail(this.taskId)
        if (res.data && res.data.code === 200) {
          const data = res.data.data
          this.form = {
            id: data.id,
            title: data.title,
            description: data.description,
            column_id: data.column_id,
            assignee_id: data.assignee_id,
            priority: data.priority,
            start_date: data.start_date,
            due_date: data.due_date,
            estimated_hours: data.estimated_hours,
            actual_hours: data.actual_hours,
            label_ids: data.labels ? data.labels.map(l => l.id) : []
          }
        }
      } catch (error) {
        console.error('获取任务详情失败:', error)
      }
    },
    async getColumns() {
      if (!this.boardId) return
      try {
        const { getBoardDetail } = await import('@/api/task')
        const res = await getBoardDetail(this.boardId)
        if (res.data && res.data.code === 200) {
          this.columns = res.data.data.columns || []
        }
      } catch (error) {
        console.error('获取列列表失败:', error)
      }
    },
    async getUserList() {
      try {
        const res = await getAdmins({ page: 1, pageSize: 100 })
        if (res.code === 200) {
          this.userList = res.data || []
        }
      } catch (error) {
        console.error('获取用户列表失败:', error)
      }
    },
    async getLabelList() {
      try {
        const res = await getLabels({ board_id: this.boardId })
        if (res.data && res.data.code === 200) {
          this.labelList = res.data.data || []
        }
      } catch (error) {
        console.error('获取标签列表失败:', error)
      }
    },
    handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        this.submitting = true
        try {
          const data = {
            ...this.form,
            board_id: this.boardId,
            project_id: this.projectId
          }
          delete data.id

          const res = this.isEdit
            ? await updateTask(this.taskId, data)
            : await createTask(data)

          if (res.data && res.data.code === 200) {
            this.$message.success(this.isEdit ? '更新成功' : '创建成功')
            this.$emit('success')
            this.$emit('update:visible', false)
          } else {
            this.$message.error(res.data.message || '操作失败')
          }
        } catch (error) {
          console.error('提交失败:', error)
          this.$message.error('操作失败')
        } finally {
          this.submitting = false
        }
      })
    },
    handleDelete() {
      this.$confirm('确认删除该任务吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await deleteTask(this.taskId)
          if (res.data && res.data.code === 200) {
            this.$message.success('删除成功')
            this.$emit('success')
            this.$emit('update:visible', false)
          }
        } catch (error) {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      })
    },
    handleClose() {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog-footer {
  display: flex;
  justify-content: flex-end;
}
</style>
